"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useHookFormAction } from "@next-safe-action/adapter-react-hook-form/hooks";
import { sendGTMEvent } from "@next/third-parties/google";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import { toast } from "sonner";

import { Button } from "~/lib/shadcn/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/lib/shadcn/components/ui/form";
import { Input } from "~/lib/shadcn/components/ui/input";
import { PhoneInput } from "~/lib/shadcn/components/ui/phone-input";

import { enquireAction } from "../actions/enquire";
import { EnquireSchema } from "../schemas/enquire-form";
import { AutosizeTextarea } from "../shadcn/components/ui/autosize-textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../shadcn/components/ui/select";

export function ContactForm({
	formLocation = "Enquire",
	membershipType = true,
}: {
	formLocation?: string;
	membershipType?: boolean;
}) {
	const { form, handleSubmitWithAction, resetFormAndAction } = useHookFormAction(
		enquireAction,
		zodResolver(EnquireSchema),
		{
			actionProps: {
				onError: () => {
					toast.error("Something went wrong!");
				},
				onSuccess: ({ input }) => {
					sendGTMEvent({ event: "ENQUIRE_SUBMISSION", value: input.email });
					toast.success("You have successfully submitted your request!");
					resetFormAndAction();
				},
			},
			errorMapProps: {},
			formProps: {
				defaultValues: {
					country: "",
					email: "",
					formLocation: formLocation ?? "Enquire",
					membershipType: membershipType ? undefined : "other",
					message: "",
					name: "",
					phoneNumber: "",
					preferredMethodOfContact: undefined,
					state: "",
				},
			},
		},
	);

	return (
		<Form {...form} key={`enquire-${form.formState.submitCount}`}>
			<form className="w-full flex space-y-6 flex-wrap items-start [&_div]:w-full" onSubmit={handleSubmitWithAction}>
				<FormField
					control={form.control}
					name="name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Name</FormLabel>
							<FormControl>
								<Input
									className="!border-b-dg-off-black placeholder:text-dg-off-black/80  text-dg-off-black"
									placeholder="Name"
									{...field}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Email</FormLabel>
							<FormControl>
								<Input
									className="!border-b-dg-off-black placeholder:text-dg-off-black/80  text-dg-off-black"
									placeholder="Email"
									{...field}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="phoneNumber"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Phone Number</FormLabel>
							<FormControl>
								<PhoneInput
									{...field}
									className="*:border-b-dg-off-black [&_input]:!border-b-dg-off-black [&_input]:!text-dg-off-black [&_input]:placeholder:text-dg-off-black/80 [&_input]:rounded-none"
									value={field.value ?? undefined}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="country"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Country</FormLabel>
							<FormControl>
								<CountryDropdown
									customRender={({ options }) => {
										return (
											<Select defaultValue={field.value} onValueChange={field.onChange}>
												<SelectTrigger className="w-full text-left px-3 py-2 text-sm data-[placeholder]:text-dg-off-black/80 border border-black rounded-none bg-transparent">
													<SelectValue placeholder="Country" />
												</SelectTrigger>
												<SelectContent className="block">
													{options.map(({ key, label, value }) => {
														if (!key || !label || !value) return null;
														return (
															<SelectItem key={key} value={value}>
																{label}
															</SelectItem>
														);
													})}
												</SelectContent>
											</Select>
										);
									}}
									onChange={field.onChange}
									value={field.value}
									valueType="short"
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="state"
					render={({ field }) => (
						<FormItem>
							<FormLabel>State</FormLabel>
							<FormControl>
								<RegionDropdown
									country={form.watch("country")}
									countryValueType="short"
									customRender={({ options }) => {
										return (
											<Select defaultValue={field.value} onValueChange={field.onChange}>
												<SelectTrigger className="w-full text-left px-3 py-2 text-sm data-[placeholder]: text-dg-off-black/80 border border-black rounded-none bg-transparent">
													<SelectValue placeholder="State" />
												</SelectTrigger>
												<SelectContent className="block">
													{options.map(({ key, label, value }) => {
														if (!key || !label || !value) return null;
														return (
															<SelectItem key={key} value={value}>
																{label}
															</SelectItem>
														);
													})}
												</SelectContent>
											</Select>
										);
									}}
									disableWhenEmpty={true}
									onChange={field.onChange}
									value={field.value ?? ""}
									valueType="short"
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="message"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Message</FormLabel>
							<FormControl>
								<AutosizeTextarea
									className="border-black placeholder:text-dg-off-black/80  text-dg-off-black"
									placeholder="Message"
									{...field}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				{membershipType && (
					<FormField
						control={form.control}
						name="membershipType"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Membership Type</FormLabel>
								<Select {...field} defaultValue={field.value} onValueChange={field.onChange}>
									<FormControl>
										<SelectTrigger className="w-full text-left px-3 py-2 text-sm data-[placeholder]:text-dg-off-black/80 border border-black rounded-none bg-transparent">
											<SelectValue placeholder="Membership Type" />
										</SelectTrigger>
									</FormControl>
									<SelectContent className="block">
										<SelectItem value="villa">Villa</SelectItem>
										<SelectItem value="corporate">Corporate</SelectItem>
										<SelectItem value="residence">Residence</SelectItem>
										<SelectItem value="other">Other</SelectItem>
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}
				<FormField
					control={form.control}
					name="preferredMethodOfContact"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Preferred Method of Contact</FormLabel>
							<Select {...field} defaultValue={field.value} onValueChange={field.onChange}>
								<FormControl>
									<SelectTrigger className="w-full text-left px-3 py-2 text-sm data-[placeholder]:text-dg-off-black/80 border border-black rounded-none bg-transparent">
										<SelectValue placeholder="Preferred Method of Contact" />
									</SelectTrigger>
								</FormControl>
								<SelectContent className="block">
									<SelectItem value="phone">Phone</SelectItem>
									<SelectItem value="email">Email</SelectItem>
									<SelectItem value="email-or-phone">Phone or Email</SelectItem>
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="whereDidYouHearAboutUs"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Where did you hear about us?</FormLabel>
							<Select {...field} defaultValue={field.value} onValueChange={field.onChange}>
								<FormControl>
									<SelectTrigger className="w-full text-left px-3 py-2 text-sm data-[placeholder]:text-dg-off-black/80 border border-black rounded-none bg-transparent">
										<SelectValue placeholder="Where did you hear about us?" />
									</SelectTrigger>
								</FormControl>
								<SelectContent className="block">
									<SelectItem value="instagram">Instagram</SelectItem>
									<SelectItem value="facebook">Facebook</SelectItem>
									<SelectItem value="linkedin">LinkedIn</SelectItem>
									<SelectItem value="google">Google</SelectItem>
									<SelectItem value="friend">Friend</SelectItem>
									<SelectItem value="media-article">Media Article</SelectItem>
									<SelectItem value="other">Other</SelectItem>
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>
				<Button
					className="!mt-8 px-12 py-4 !bg-[#3C3A2C] text-white uppercase font-skia tracking-widest rounded-none"
					disabled={form.formState.isSubmitting || form.formState.isLoading || form.formState.isValidating}
					type="submit"
				>
					Enquire
				</Button>
			</form>
		</Form>
	);
}

export default function ContactFormRenderer() {
	return (
		<div className="max-w-screen-sm mx-auto gap-8 flex flex-col py-20">
			{/* {data.heading && <h2 className="text-xl uppercase font-semibold leading-none mb-0">{data.heading}</h2>}
			{data.beforeText && <RichText className="flex flex-col gap-4 richtext" data={data.beforeText} />} */}
			<ContactForm membershipType />
			{/* {data.afterText && <RichText className="flex flex-col gap-4 text-center richtext" data={data.afterText} />} */}
		</div>
	);
}
